'use client'

import { useEffect, useRef } from 'react'
import { IconChevronLeft } from '@tabler/icons-react'
import clsx from 'clsx'
import { usePathname, useSearchParams } from 'next/navigation'

import { useOverlayStack } from 'shared/contexts/OverlayStackContext'
import Button from 'shared/ui/Button'
import { ButtonVariants } from 'shared/ui/Button/Button.enums'
import { useFocusTrap } from 'shared/ui/hooks/useFocusTrap'
import { KEYBOARD_KEYS } from 'shared/ui/types'
import { addTestId } from 'shared/utils/tests'

import styles from './SearchContainer.module.scss'

interface SearchContainerProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
}

const searchDropdownId = 'search-dropdown'

const SearchContainer = ({
  isOpen,
  onClose,
  children
}: SearchContainerProps) => {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const lastUrlRef = useRef<string | null>(null)
  const searchWrapperRef = useRef<HTMLDivElement>(null)
  const { register, unregister, isTop } = useOverlayStack()

  // Register/unregister search container in stack during lifecycle
  useEffect(() => {
    if (!isOpen) return
    register({ id: searchDropdownId, type: 'modal' }) // Search container uses modal type for scroll locking

    return () => unregister(searchDropdownId)
  }, [isOpen, register, unregister])

  // Close dropdown when navigation happens
  useEffect(() => {
    const currentUrl = `${pathname}?${searchParams?.toString() ?? ''}`

    if (!isOpen) {
      lastUrlRef.current = currentUrl

      return
    }

    if (lastUrlRef.current !== null && lastUrlRef.current !== currentUrl) {
      onClose()
    }

    lastUrlRef.current = currentUrl
  }, [pathname, searchParams, isOpen, onClose])

  useEffect(() => {
    if (!isOpen) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== KEYBOARD_KEYS.ESCAPE || !isTop(searchDropdownId)) return
      e.preventDefault()
      e.stopPropagation()
      onClose()
    }

    window.addEventListener('keydown', onKeyDown)

    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose, isTop])

  useFocusTrap({
    container: searchWrapperRef,
    active: isOpen
  })

  // Close dropdown on click outside search wrapper.
  useEffect(() => {
    if (!isOpen || !isTop(searchDropdownId)) return

    const handleDocumentPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null

      if (!target) return
      if (searchWrapperRef.current?.contains(target)) return

      onClose()
    }

    document.addEventListener('mousedown', handleDocumentPointerDown)
    document.addEventListener('touchstart', handleDocumentPointerDown)

    return () => {
      document.removeEventListener('mousedown', handleDocumentPointerDown)
      document.removeEventListener('touchstart', handleDocumentPointerDown)
    }
  }, [isOpen, onClose, isTop])

  return (
    <>
      <div
        className={clsx(styles.backdrop, { [styles.backdropOpen]: isOpen })}
        onClick={onClose}
        aria-hidden={!isOpen}
      />
      <div
        ref={searchWrapperRef}
        className={clsx(styles.searchWrapper, { [styles.active]: isOpen })}
        {...addTestId('search')}
      >
        <div className={styles.searchContent}>
          {isOpen && (
            <div className={styles.mobileReturnButtonWrapper}>
              <Button
                startIcon={<IconChevronLeft />}
                onClick={onClose}
                variant={ButtonVariants.neutralText}
                aria-label='Zamknij wyszukiwanie'
                {...addTestId('search-return-button')}
              />
            </div>
          )}
          {children}
        </div>
      </div>
    </>
  )
}

export default SearchContainer
